<template>
  <div class="password-step">

    <div class="fm-info-banner mb-4">
      <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
      </svg>
      <span>Create a password to access your Flash Money account and track your application.</span>
    </div>

    <FormGroup :error-container="errorContainerName" label="New Password" name="password" :floating="true">
      <input v-model="form.password" class="form-control" :type="showPassword ? 'text' : 'password'" placeholder=" "/>
    </FormGroup>

    <!-- Password strength bar -->
    <div v-if="form.password" class="pwd-strength mb-3">
      <div class="pwd-strength__bar">
        <div class="pwd-strength__fill" :class="`pwd-strength__fill--${strength.level}`" :style="{ width: strength.pct + '%' }"/>
      </div>
      <span class="pwd-strength__label" :class="`pwd-strength__label--${strength.level}`">{{ strength.label }}</span>
    </div>

    <FormGroup :error-container="errorContainerName" label="Confirm Password" name="password_confirmation" :floating="true">
      <input v-model="form.password_confirmation" class="form-control" :type="showPassword ? 'text' : 'password'" placeholder=" "/>
    </FormGroup>

    <label class="fm-toggle-show">
      <input type="checkbox" v-model="showPassword"/> Show password
    </label>

    <slot name="controls" v-bind="{ prev: onPrev, next: onNext }"/>
  </div>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import store from '@modules/guest/new-applicant/store/store';
import {NEW_APPLICATION} from '@store/constants';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import {validate} from './formValidator';
import {saveAsDraft} from '@modules/guest/new-applicant/NewApplicationService';

const ERROR_CONTAINER_NAME = 'NewApplicationPasswordStepForm';

const calcStrength = (pwd) => {
  if (!pwd) return { level: 'none', label: '', pct: 0 };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { level: 'weak', label: 'Weak', pct: 25 };
  if (score <= 2) return { level: 'fair', label: 'Fair', pct: 50 };
  if (score <= 3) return { level: 'good', label: 'Good', pct: 75 };
  return { level: 'strong', label: 'Strong', pct: 100 };
};

export default {
  name: 'Password',
  mixins: [NewApplicationStepMixin],
  components: {FormGroup},
  computed: {
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    strength() { return calcStrength(this.form.password); },
  },
  data() {
    return {
      form: { password: '', password_confirmation: '' },
      showPassword: false,
    };
  },
  watch: {
    form: {
      deep: true, immediate: true,
      handler() { if (this.shouldGoNextStep()) this.syncWithStore(); },
    },
  },
  methods: {
    onNext() {
      if (!this.shouldGoNextStep()) { scrollToErrorFormField(this.$el); return; }
      this.saveAndSync().catch(e => { this.handleServerError(e); scrollToErrorFormField(this.$el); });
    },
    async saveAndSync() {
      const payload = { step: 'Password', ...this.form };
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, loanNumber, canPerformCS}} = await saveAsDraft(this.code, payload);
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp, loanNumber, canPerformCS}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
        if (!loanNumber || !canPerformCS) { await store.dispatch(NEW_APPLICATION.ACTIONS.DONE_STEP); return; }
        if (loanNumber && canPerformCS) {
          await store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {submitted: true}});
          await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
        }
      } catch (e) { throw e; }
      finally { store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    onPrev() { store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP); },
    syncWithStore() {
      this.$store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_PASSWORD, payload: { ...this.form } });
    },
    validate,
  },
  created() { this.registerErrorContainer(ERROR_CONTAINER_NAME); },
};
</script>

<style lang="scss">
$amber:    #FFB000;
$ink:      #15171C;
$ink-soft: #5B606B;
$line:     #E7E4DA;

.pwd-strength {
  display: flex;
  align-items: center;
  gap: 10px;

  &__bar {
    flex: 1;
    height: 4px;
    background: $line;
    border-radius: 100px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    border-radius: 100px;
    transition: width 0.3s ease, background 0.3s;

    &--weak   { background: #ef4444; }
    &--fair   { background: #f97316; }
    &--good   { background: #eab308; }
    &--strong { background: #22c55e; }
  }

  &__label {
    font-size: 11.5px;
    font-weight: 600;
    min-width: 44px;
    text-align: right;

    &--weak   { color: #ef4444; }
    &--fair   { color: #f97316; }
    &--good   { color: #ca8a04; }
    &--strong { color: #16a34a; }
  }
}

.fm-toggle-show {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: $ink-soft;
  cursor: pointer;
  margin-bottom: 1.25rem;
  user-select: none;

  input[type="checkbox"] {
    accent-color: $amber;
    width: 15px;
    height: 15px;
    cursor: pointer;
  }
}
</style>
